import { useState } from "react";
import { useSite } from "@/context/SiteContext";
import { MapPin, ChevronDown, Check, Lock } from "lucide-react";

/**
 * SiteSwitcher — top-bar pill that shows the currently-active site and,
 * for multi-site admins, lets them switch. Non-admins see a locked pill
 * (their single site). Switching triggers a full reload so every fetched
 * dataset re-runs with the new `X-Site-Override` header.
 */
export default function SiteSwitcher() {
  const { activeSite, activeSiteObj, sites, canSwitch, switchSite } = useSite();
  const [open, setOpen] = useState(false);

  if (!activeSite) return null;

  const label = activeSiteObj?.site_name || activeSite;
  const list = sites || [];

  if (!canSwitch) {
    return (
      <div
        className="pill hidden md:inline-flex !py-1.5 !px-3"
        title={`Site-scoped to ${label}`}
        data-testid="site-switcher-locked"
      >
        <MapPin className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
        <span className="font-mono-tight text-xs">{activeSite}</span>
        <Lock className="w-3 h-3 opacity-60" />
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="pill !py-1.5 !px-3 hover:bg-white/5"
        style={{ borderColor: "var(--primary)" }}
        data-testid="site-switcher-btn"
        title="Switch site"
      >
        <MapPin className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
        <span className="font-mono-tight text-xs">{activeSite}</span>
        <span className="hidden lg:inline text-xs truncate max-w-[120px]">· {label}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-50 panel-heavy p-1.5 w-72 max-h-[70vh] overflow-auto"
            data-testid="site-switcher-menu"
          >
            <div className="text-[10px] uppercase tracking-widest text-muted px-3 pt-2 pb-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Switch site scope
            </div>
            {list.map((s) => {
              const on = s.code === activeSite;
              return (
                <button
                  key={s.code}
                  onClick={() => { setOpen(false); switchSite(s.code); }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 hover:bg-white/5 ${on ? "" : ""}`}
                  style={on ? { background: "rgba(245,130,32,0.10)" } : {}}
                  data-testid={`site-switcher-item-${s.code}`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                       style={on
                         ? { background: "var(--primary)", color: "#0a0a0a" }
                         : { background: "var(--surface-2)", color: "var(--primary)", border: "1px solid var(--border)" }}>
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-sm truncate">{s.site_name || s.name || s.code}</div>
                    <div className="text-[10px] text-muted font-mono-tight truncate">
                      {s.code}{s.company ? ` · ${s.company}` : ""}
                    </div>
                  </div>
                  {on && <Check className="w-4 h-4" style={{ color: "var(--primary)" }} />}
                </button>
              );
            })}
            <div className="text-[10px] uppercase tracking-widest text-muted px-3 pt-2 pb-1 opacity-70">
              Switching reloads the portal
            </div>
          </div>
        </>
      )}
    </div>
  );
}
